/**
 * Studio-specific HTML report generator.
 * Converts Dexie Run + RunStep records into a self-contained HTML file.
 * No external dependencies — CSS and JS are inlined.
 * Screenshots embedded as base64 data URIs when available via OPFS.
 */

import type { Run, RunStep } from "../db/schema.js";

// ── OPFS screenshot reader ────────────────────────────────────────────────────

export async function readScreenshotAsBase64(screenshotRef: string): Promise<string | null> {
  try {
    const root = await navigator.storage.getDirectory();
    const parts = screenshotRef.split("/");
    let dir: FileSystemDirectoryHandle = root;
    for (const part of parts.slice(0, -1)) {
      dir = await dir.getDirectoryHandle(part);
    }
    const fileHandle = await dir.getFileHandle(parts[parts.length - 1]!);
    const file = await fileHandle.getFile();
    const buf = await file.arrayBuffer();
    const bytes = new Uint8Array(buf);
    let binary = "";
    for (let i = 0; i < bytes.length; i++) binary += String.fromCharCode(bytes[i]!);
    return btoa(binary);
  } catch {
    return null; // screenshot not available (Phase 3 OPFS not yet populated)
  }
}

// ── HTML generation ───────────────────────────────────────────────────────────

function statusBadge(status: string): string {
  const map: Record<string, [string, string]> = {
    ok:           ["#a6e3a1", "✓"],
    passed:       ["#a6e3a1", "✓ Passed"],
    not_found:    ["#f9e2af", "⚠ Not Found"],
    error:        ["#f38ba8", "✗ Error"],
    passed_healed:["#89b4fa", "⚕ Healed"],
    failed:       ["#f38ba8", "✗ Failed"],
  };
  const [color, label] = map[status] ?? ["#8b8fa8", status];
  return `<span style="background:${color}22;color:${color};padding:2px 8px;border-radius:4px;font-size:0.75rem;font-weight:600;">${label}</span>`;
}

function fmtDate(ts: number): string {
  return new Date(ts).toLocaleString();
}

function fmtDuration(ms: number): string {
  if (ms < 1000) return `${ms}ms`;
  return `${(ms / 1000).toFixed(1)}s`;
}

export async function generateRunHtml(
  run: Run,
  steps: RunStep[],
  nameMap?: Map<string, string>,
): Promise<string> {
  // Load screenshots from OPFS for steps that have refs
  const screenshots = new Map<string, string>();
  for (const step of steps) {
    if (step.screenshotRef) {
      const b64 = await readScreenshotAsBase64(step.screenshotRef);
      if (b64) screenshots.set(step.id, b64);
    }
  }

  const durationMs = run.finishedAt - run.startedAt;
  const runDate = fmtDate(run.startedAt);
  const overallColor = run.status === "passed" || run.status === "passed_healed"
    ? "#a6e3a1" : "#f38ba8";

  const stepRows = steps
    .sort((a, b) => a.stepIndex - b.stepIndex)
    .map((step) => {
      const img = screenshots.get(step.id)
        ? `<img src="data:image/png;base64,${screenshots.get(step.id)}" class="step-thumb" onclick="mimiqZoom(this)" title="Click to view full screen" />`
        : `<span style="color:#45475a;font-size:0.7rem;">—</span>`;

      const coords = step.x !== undefined && step.y !== undefined
        ? `<span style="color:#585b70;font-size:0.7rem;">(${step.x},${step.y})</span>` : "";
      const matchedBy = step.matchedBy
        ? `<span style="background:#313244;color:#89b4fa;padding:1px 5px;border-radius:3px;font-size:0.68rem;">${step.matchedBy}</span>` : "";
      const healHint = step.healHint
        ? `<div style="color:#89b4fa;font-size:0.7rem;margin-top:3px;">⚕ ${step.healHint}</div>` : "";
      const rawStepName = nameMap?.get(step.stepId) ?? "—";
      // Mask sensitive text in the exported report (🔒 prefix is set by irStepName)
      const stepName = rawStepName;
      const a11yBlock = (step.a11yDump && step.status !== "ok")
        ? `<details style="margin-top:4px;"><summary style="font-size:0.68rem;color:#f59e0b;cursor:pointer;">📋 Accessibility Tree (expand)</summary><pre style="font-size:0.62rem;font-family:monospace;background:#11111b;border:1px solid #313244;border-radius:4px;padding:6px;margin-top:4px;max-height:160px;overflow:auto;white-space:pre-wrap;word-break:break-all;color:#cdd6f4;">${escapeHtml(step.a11yDump)}</pre></details>`
        : "";

      return `<tr>
        <td style="text-align:center;color:#585b70;">${step.stepIndex + 1}</td>
        <td><code style="background:#313244;padding:2px 5px;border-radius:3px;font-size:0.75rem;">${step.stepType}</code></td>
        <td style="font-size:0.82rem;color:#cdd6f4;max-width:200px;">${escapeHtml(stepName)}</td>
        <td>${statusBadge(step.status)}</td>
        <td style="font-size:0.8rem;color:#cdd6f4;">
          ${step.detail ?? ""}${healHint}
          <div style="margin-top:2px;">${matchedBy} ${coords}</div>
          ${a11yBlock}
        </td>
        <td>${img}</td>
      </tr>`;
    }).join("\n");

  return `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width,initial-scale=1">
  <title>Mimiq Report — ${escapeHtml(run.testCaseName)}</title>
  <style>
    *{box-sizing:border-box;margin:0;padding:0}
    body{background:#1e1e2e;color:#cdd6f4;font-family:-apple-system,BlinkMacSystemFont,"Segoe UI",sans-serif;padding:1.5rem}
    h1{font-size:1.4rem;margin-bottom:0.4rem}
    .meta{color:#8b8fa8;font-size:0.82rem;margin-bottom:1rem}
    .meta span{margin-right:1.2rem}
    .overall{display:inline-block;padding:0.35rem 1rem;border-radius:6px;font-weight:700;font-size:0.88rem;margin-bottom:1.5rem}
    .summary{display:flex;gap:1.2rem;margin-bottom:1.5rem;flex-wrap:wrap}
    .summary-item{background:#313244;border-radius:8px;padding:0.6rem 1rem;text-align:center;min-width:80px}
    .summary-item .num{font-size:1.4rem;font-weight:700;display:block}
    .summary-item .lbl{font-size:0.7rem;color:#8b8fa8;text-transform:uppercase}
    table{width:100%;border-collapse:collapse;background:#181825;border-radius:8px;overflow:hidden}
    th{background:#313244;padding:0.5rem 0.75rem;text-align:left;font-size:0.75rem;color:#8b8fa8;text-transform:uppercase;letter-spacing:0.05em}
    td{padding:0.55rem 0.75rem;border-bottom:1px solid #313244;vertical-align:middle}
    tr:last-child td{border-bottom:none}
    tr:hover td{background:#1e1e2e22}
    footer{margin-top:1.5rem;color:#45475a;font-size:0.72rem;text-align:center}
    /* Screenshot thumbnail + lightbox */
    .step-thumb{max-width:120px;max-height:80px;border-radius:4px;cursor:zoom-in;border:1px solid #313244;transition:opacity 0.1s;}
    .step-thumb:hover{opacity:0.85;}
    #mq-overlay{position:fixed;inset:0;background:rgba(0,0,0,0.88);z-index:9999;display:none;align-items:center;justify-content:center;cursor:zoom-out;}
    #mq-overlay.open{display:flex;}
    #mq-overlay img{max-width:92vw;max-height:92vh;border-radius:8px;box-shadow:0 8px 40px rgba(0,0,0,0.6);}
    #mq-overlay .close-btn{position:fixed;top:1rem;right:1.5rem;background:rgba(255,255,255,0.15);border:none;color:#fff;font-size:1.6rem;cursor:pointer;border-radius:50%;width:2.2rem;height:2.2rem;line-height:2.2rem;text-align:center;}
    #mq-overlay .close-btn:hover{background:rgba(255,255,255,0.25);}
  </style>
  <script>
    function mimiqZoom(img) {
      var ov = document.getElementById('mq-overlay');
      var fi = document.getElementById('mq-full-img');
      fi.src = img.src;
      ov.classList.add('open');
    }
    document.addEventListener('DOMContentLoaded', function() {
      var ov = document.getElementById('mq-overlay');
      ov.addEventListener('click', function(e) {
        if (e.target === ov || e.target.classList.contains('close-btn')) {
          ov.classList.remove('open');
        }
      });
      document.addEventListener('keydown', function(e) {
        if (e.key === 'Escape') ov.classList.remove('open');
      });
    });
  </script>
</head>
<body>
  <!-- Lightbox overlay -->
  <div id="mq-overlay">
    <button class="close-btn" onclick="document.getElementById('mq-overlay').classList.remove('open')">✕</button>
    <img id="mq-full-img" src="" alt="Screenshot" />
  </div>
  <h1>${escapeHtml(run.testCaseName)}</h1>
  ${(run.tags ?? []).length > 0 ? `<div style="margin-bottom:0.5rem;">${(run.tags ?? []).map(t => `<span style="display:inline-block;font-size:0.68rem;padding:1px 8px;border-radius:10px;background:#6366f122;color:#6366f1;border:1px solid #6366f144;margin-right:4px;">${escapeHtml(t)}</span>`).join("")}</div>` : ""}
  <div class="meta">
    <span>📅 ${runDate}</span>
    <span>⏱ ${fmtDuration(durationMs)}</span>
    ${run.deviceModel ? `<span>📱 ${escapeHtml(run.deviceModel)}</span>` : ""}
    ${run.androidVersion ? `<span>🤖 Android ${escapeHtml(run.androidVersion)}</span>` : ""}
    ${run.deviceSerial ? `<span style="color:#45475a;">${escapeHtml(run.deviceSerial)}</span>` : ""}
  </div>
  <div class="overall" style="background:${overallColor}22;color:${overallColor};">
    ${statusBadge(run.status).replace(/<[^>]+>/g, "")} — ${run.passedCount}/${run.stepCount} steps passed
    ${run.healCount > 0 ? `· ${run.healCount} healed` : ""}
  </div>
  <div class="summary">
    <div class="summary-item"><span class="num" style="color:#a6e3a1">${run.passedCount}</span><span class="lbl">Passed</span></div>
    <div class="summary-item"><span class="num" style="color:#f38ba8">${run.failedCount}</span><span class="lbl">Failed</span></div>
    ${run.healCount > 0 ? `<div class="summary-item"><span class="num" style="color:#89b4fa">${run.healCount}</span><span class="lbl">Healed</span></div>` : ""}
    <div class="summary-item"><span class="num">${run.stepCount}</span><span class="lbl">Total</span></div>
  </div>
  <table>
    <thead><tr>
      <th style="width:40px">#</th>
      <th style="width:100px">Type</th>
      <th style="width:180px">Step Name</th>
      <th style="width:110px">Status</th>
      <th>Detail</th>
      <th style="width:200px">Screenshot</th>
    </tr></thead>
    <tbody>${stepRows}</tbody>
  </table>
  <footer>Generated by Mimiq Studio · ${new Date().toISOString()}</footer>
</body>
</html>`;
}

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}
